import React from 'react';
import {
  SUPPORT_STACK_MODULES,
  SUPPORT_STACK_OPERATING_MODES,
  supportStackFamilyId,
} from '@cleic/config/support-stack';
import { appendCleicAppChannel, resolveCleicAppUrl } from '../app-channel.js';
import SectionCard from '../components/SectionCard.jsx';
import { makeShowBlock, SubViewBreadcrumb } from '../components/SubViewShell.jsx';

const SUPPORT_FAMILIES = [
  {
    id: 'sub-ledgers',
    label: 'Sub-ledgers',
    purpose: 'Accounting support ledgers behind GL truth.',
    boundary: 'They explain balances and movements; actual only when posted as a Ledger JE.',
    surfaces: [
      { label: 'Actuals formal proof', tab: 'actuals', section: 'formal' },
      { label: 'Command NC assets', tab: 'fcc', section: 'nc-assets-liabilities' },
      { label: 'Ledger projection proof', tab: 'synthesis', section: 'ledger-projection' },
    ],
    modes: {
      ACTUALS: 'Read posted support evidence from Ledger.',
      BUDGETS: 'Explain planned support movement by period.',
      FORECASTS: 'Project future movement without writing actuals.',
    },
  },
  {
    id: 'schedules',
    label: 'Schedules',
    purpose: 'Time-based recognition, allocation, repayment, and valuation plans.',
    boundary: 'Rows guide timing; posted Ledger JEs remain accounting truth.',
    surfaces: [
      { label: 'Budgets recurring cadence', tab: 'budgets', section: 'recurring' },
      { label: 'Drivers schedules and pins', tab: 'drivers', section: 'piecewise' },
      { label: 'Forecast controls', tab: 'forecasts', section: 'model-assumptions' },
    ],
    modes: {
      ACTUALS: 'Back-test schedule timing against Ledger postings.',
      BUDGETS: 'Carry recurring recognition into budget periods.',
      FORECASTS: 'Drive forward timing, allocation, and valuation curves.',
    },
  },
  {
    id: 'trackers',
    label: 'Trackers',
    purpose: 'Operational monitor lanes for status, variance, exceptions, and run history.',
    boundary: 'They detect and explain drift; they do not create actuals by themselves.',
    surfaces: [
      { label: 'Cash warnings', tab: 'cash-monitor', section: 'warnings' },
      { label: 'Variance waterfall', tab: 'synthesis', section: 'variance' },
      { label: 'Command transactions', tab: 'fcc', section: 'transactions' },
    ],
    modes: {
      ACTUALS: 'Monitor actual exceptions from Ledger-backed surfaces.',
      BUDGETS: 'Track budget variance and readiness states.',
      FORECASTS: 'Surface risk, drift, and scenario alerts.',
    },
  },
  {
    id: 'write-intents',
    label: 'Write Intents',
    purpose: 'Impact-ranked proposals that can become Ledger actions.',
    boundary: 'Lens can model and explain intent; Ledger records the audited write or amendment.',
    surfaces: [
      { label: 'Report amendment handoff', tab: 'support-stack', section: 'write-intents' },
      { label: 'Ledger projection proof', tab: 'synthesis', section: 'ledger-projection' },
      { label: 'Command transactions', tab: 'fcc', section: 'transactions' },
    ],
    modes: {
      ACTUALS: 'Keep proposed actual writes outside Lens until Ledger approval.',
      BUDGETS: 'Annotate plan changes that should not mutate actuals.',
      FORECASTS: 'Rank scenario changes before audited handoff.',
    },
  },
];

const SUPPORT_MODULES = SUPPORT_STACK_MODULES.map((module) => ({
  label: module.label,
  family: supportStackFamilyId(module),
  lensUse: module.lensUse,
  actualsUse: module.operatingSpine.ACTUALS,
  budgetsUse: module.operatingSpine.BUDGETS,
  forecastsUse: module.operatingSpine.FORECASTS,
  boundary: module.actualsBoundary,
}));

const OPERATING_MODES = SUPPORT_STACK_OPERATING_MODES;

const FAMILY_LABELS = Object.fromEntries(SUPPORT_FAMILIES.map((family) => [family.id, family.label]));

const WRITE_INTENT_ROWS = [
  {
    intent: 'Report amendment',
    impact: 'Changed assumption, late actual, or post-close adjustment that affects a frozen report narrative.',
    handoff: 'Ledger Report Snapshot amendment panel on the frozen snapshot.',
    ledgerPath: '/reports/snapshots',
    contract: 'report-snapshot-amendment',
    custody: 'reason + signed-in Ledger actor + frozen snapshot lineage',
    evidence: 'Snapshot stays frozen; amendment reason and signed-in Ledger context explain the new lineage.',
  },
  {
    intent: 'Adjusting JE proposal',
    impact: 'Closed-period correction, reclassification, accrual, deferral, or reversal that changes actuals.',
    handoff: 'Ledger JE create, void, restore, or supersede after operator approval.',
    ledgerPath: '/journal-entries',
    contract: 'journal-entry-non-destructive-revisability',
    custody: 'reason + activity trail + linked impact + snapshot warnings',
    evidence: 'Activity, linked JE impact, affected snapshots, and audit trail preserve current truth plus history.',
  },
  {
    intent: 'Schedule recognition',
    impact: 'Prepayment, deferral, depreciation, amortization, loan interest, or valuation timing reaches a posting point.',
    handoff: 'Ledger-generated or manual JE after review of the supporting schedule.',
    ledgerPath: '/schedules',
    contract: 'support-stack-schedule-review',
    custody: 'support row + operator review + posted JE authority',
    evidence: 'Schedule remains support evidence; only the posted JE becomes accounting truth.',
  },
  {
    intent: 'Tracker exception',
    impact: 'Variance, aging, cash warning, threshold breach, or run-status drift needs attention.',
    handoff: 'No automatic write; operator chooses the Ledger action or leaves it as monitored evidence.',
    ledgerPath: '/trackers',
    contract: 'support-stack-exception-monitor',
    custody: 'exception context + chosen Ledger action, if any',
    evidence: 'Tracker history remains read-only until an audited Ledger action is taken.',
  },
  {
    intent: 'Budget or Forecast carry-forward',
    impact: 'Scenario change affects projected statements, covenant views, or management planning surfaces.',
    handoff: 'No Ledger actual write; optional report amendment note if the change explains a published view.',
    ledgerPath: '/reports/snapshots',
    contract: 'scenario-to-report-explanation',
    custody: 'model state stays in Lens; Ledger records actuals/amendments only',
    evidence: 'Budgets and Forecasts remain model state until Ledger actuals or report amendments are recorded.',
  },
];

const WRITE_INTENT_GUARDRAILS = [
  ['Lens writes posted actuals', 'No. Proposals stay in Lens until Ledger action.'],
  ['Ledger owns audited writes', 'Yes. Writes happen in Ledger with audit and snapshot lineage.'],
  ['Report amendment author comes from Ledger sign-in context', 'Yes. The submitted form cannot override it.'],
];

function SurfaceButton({ surface, onJump }) {
  return (
    <button
      type="button"
      className="rounded-sm border border-border bg-surface-el px-2.5 py-1 text-left text-[0.75rem] font-medium text-text-muted transition-colors hover:border-accent-gold hover:text-text"
      onClick={() => onJump?.({ tab: surface.tab, section: surface.section })}
    >
      {surface.label}
    </button>
  );
}

export default function SupportStackTab({ activeSection, onBackToOverview, onJump }) {
  const show = makeShowBlock(activeSection);

  return (
    <div>
      <SubViewBreadcrumb
        moduleId="support-stack"
        activeSection={activeSection}
        onBack={onBackToOverview}
      />

      {show('sub-ledgers', 'schedules', 'trackers', 'write-intents') && (
        <SectionCard
          title="Universal Support Stack"
          subtitle="ACTUALS, BUDGETS, and FORECASTS share one Lens shell for Sub-ledgers, Schedules, Trackers, and Write Intents. Ledger remains the system of record."
          accent="#22c55e"
        >
          <div className="grid gap-3 lg:grid-cols-4">
            {SUPPORT_FAMILIES.map((family) => (
              <section
                key={family.id}
                className="min-w-0 border-l-[3px] border-border-strong bg-surface-el px-3 py-2"
              >
                <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-text-dim">
                  {family.label}
                </p>
                <p className="mt-2 text-sm font-medium text-text">{family.purpose}</p>
                <p className="mt-2 text-[0.78rem] text-text-muted">{family.boundary}</p>
                <div className="mt-3 space-y-1 text-[0.72rem] text-text-muted">
                  {OPERATING_MODES.map((mode) => (
                    <p key={mode}>
                      <span className="font-semibold text-text">{mode}</span>: {family.modes[mode]}
                    </p>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {family.surfaces.map((surface) => (
                    <SurfaceButton key={surface.label} surface={surface} onJump={onJump} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </SectionCard>
      )}

      {show('write-intents') && (
        <SectionCard
          sectionId="write-intents"
          title="Write Intents"
          subtitle="Impact proposals and Ledger handoffs. Lens explains the intended change; Ledger owns audited writes and report amendments."
        >
          <WriteIntentShell />
        </SectionCard>
      )}

      {show('sub-ledgers') && (
        <SectionCard
          sectionId="sub-ledgers"
          title="Sub-ledgers"
          subtitle="Support ledgers explain GL balances for loans, fixed assets, intangibles, and carrying accounts."
        >
          <SupportModuleTable familyId="sub-ledgers" />
        </SectionCard>
      )}

      {show('schedules') && (
        <SectionCard
          sectionId="schedules"
          title="Schedules"
          subtitle="Recognition, repayment, allocation, and valuation timing plans that feed Budgets and Forecasts without becoming actuals."
        >
          <SupportModuleTable familyId="schedules" />
        </SectionCard>
      )}

      {show('trackers') && (
        <SectionCard
          sectionId="trackers"
          title="Trackers"
          subtitle="Operational monitor lanes for exceptions, variance, period-end status, and run history."
        >
          <SupportModuleTable familyId="trackers" />
        </SectionCard>
      )}
    </div>
  );
}

function WriteIntentShell() {
  const ledgerBaseUrl = resolveCleicAppUrl('ledger');
  return (
    <div data-lens-write-intent-shell="support-stack" className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        {WRITE_INTENT_GUARDRAILS.map(([label, value]) => (
          <div key={label} className="border-l-[3px] border-accent-gold bg-surface-el px-3 py-2">
            <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-text-dim">
              Guardrail
            </p>
            <p className="mt-2 text-sm font-medium text-text">{label}</p>
            <p className="mt-1 text-[0.82rem] text-text-muted">{value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[940px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-[0.68rem] uppercase tracking-[0.06em] text-text-dim">
              <th className="py-2 pr-4 text-left font-semibold">Intent</th>
              <th className="px-4 py-2 text-left font-semibold">Impact focus</th>
              <th className="px-4 py-2 text-left font-semibold">Ledger handoff</th>
              <th className="px-4 py-2 text-left font-semibold">Handoff contract</th>
              <th className="py-2 pl-4 text-left font-semibold">Evidence rule</th>
            </tr>
          </thead>
          <tbody>
            {WRITE_INTENT_ROWS.map((row) => {
              const ledgerHref = appendCleicAppChannel(`${ledgerBaseUrl}${row.ledgerPath}`);
              return (
                <tr key={row.intent} className="border-b border-border last:border-b-0">
                  <td className="py-3 pr-4 font-medium text-text">{row.intent}</td>
                  <td className="px-4 py-3 text-text-muted">{row.impact}</td>
                  <td className="px-4 py-3 text-text-muted">
                    <p>{row.handoff}</p>
                    <a
                      href={ledgerHref}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex rounded-sm border border-border bg-surface-el px-2 py-1 text-[0.72rem] font-semibold text-text-muted hover:border-accent-gold hover:text-text"
                    >
                      Open Ledger
                    </a>
                  </td>
                  <td className="px-4 py-3 text-text-muted">
                    <p className="font-medium text-text">{row.contract}</p>
                    <p className="mt-1 text-[0.78rem]">{row.custody}</p>
                  </td>
                  <td className="py-3 pl-4 text-text-muted">{row.evidence}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SupportModuleTable({ familyId }) {
  const rows = SUPPORT_MODULES.filter((module) => module.family === familyId);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1040px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-[0.68rem] uppercase tracking-[0.06em] text-text-dim">
            <th className="py-2 pr-4 text-left font-semibold">Module</th>
            <th className="px-4 py-2 text-left font-semibold">Family</th>
            <th className="px-4 py-2 text-left font-semibold">Lens use</th>
            <th className="px-4 py-2 text-left font-semibold">ACTUALS</th>
            <th className="px-4 py-2 text-left font-semibold">BUDGETS</th>
            <th className="px-4 py-2 text-left font-semibold">FORECASTS</th>
            <th className="py-2 pl-4 text-left font-semibold">Actuals boundary</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((module) => (
            <tr key={module.label} className="border-b border-border last:border-b-0">
              <td className="py-3 pr-4 font-medium text-text">{module.label}</td>
              <td className="px-4 py-3 text-text-muted">{FAMILY_LABELS[module.family]}</td>
              <td className="px-4 py-3 text-text-muted">{module.lensUse}</td>
              <td className="px-4 py-3 text-text-muted">{module.actualsUse}</td>
              <td className="px-4 py-3 text-text-muted">{module.budgetsUse}</td>
              <td className="px-4 py-3 text-text-muted">{module.forecastsUse}</td>
              <td className="py-3 pl-4 text-text-muted">{module.boundary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
